// Navigation model (spec §11, ARCHITECTURE §1): Home · Money · Health · Records · More.
// Foundation-owned. Module agents: if you add a brand-new screen that needs a nav entry, add it here
// (minimal additive change) and mention it in your report. Paths are relative to /w/:wsId.
import {
  Activity,
  BadgePoundSterling,
  Banknote,
  Bell,
  BookOpen,
  CalendarClock,
  ClipboardList,
  FileText,
  FileUp,
  Gift,
  HandCoins,
  HeartPulse,
  HelpCircle,
  Home,
  Landmark,
  LayoutGrid,
  LifeBuoy,
  ListChecks,
  Lock,
  MoreHorizontal,
  NotebookPen,
  Receipt,
  Scale,
  ScrollText,
  ShieldCheck,
  Stethoscope,
  UserRound,
  Users,
  Wallet,
  Archive,
  Download,
  CreditCard,
  type LucideIcon,
} from 'lucide-react';
import type { EnabledModules, PermissionSubject, WorkspaceDetail } from '@seventide/types';

export type SectionKey = 'home' | 'money' | 'health' | 'records' | 'more';

export interface NavItem {
  /** Path relative to /w/:wsId (no leading slash). */
  path: string;
  label: string;
  /** One line from spec §11 — shown on landing cards and placeholder pages. */
  description: string;
  icon: LucideIcon;
  /** Permission subject needed to view (and, for visibility modules, to be switched on). */
  subject?: PermissionSubject;
  /** Release the screen belongs to, when not R1. */
  release?: 'R2' | 'R3';
}

export interface NavSection {
  key: SectionKey;
  label: string;
  /** Landing path relative to /w/:wsId ('' = Home). */
  path: string;
  icon: LucideIcon;
  description: string;
  items: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    key: 'home',
    label: 'Home',
    path: '',
    icon: Home,
    description: 'What needs attention, key figures for the current stage and recent activity.',
    items: [
      {
        path: 'this-week',
        label: 'This week',
        description: 'Actions due across every area, in priority order, with one-tap completion.',
        icon: CalendarClock,
      },
    ],
  },
  {
    key: 'money',
    label: 'Money',
    path: 'money',
    icon: Wallet,
    description: 'Accounts, transactions, gifts, tax years, the estate estimate and care finances.',
    items: [
      { path: 'money/accounts', label: 'Accounts', description: 'Accounts and balances; add or update a balance; import a statement.', icon: Landmark, subject: 'money' },
      { path: 'money/transactions', label: 'Transactions', description: 'Every payment in and out, with filters, bulk categorising, attorney attribution and receipts.', icon: Receipt, subject: 'money' },
      { path: 'money/import', label: 'Import a statement', description: 'Upload a bank CSV, match the columns, skip duplicates and categorise in bulk.', icon: FileUp, subject: 'money' },
      { path: 'money/gifts', label: 'Gifts', description: 'Gifts by tax year with exemptions, seven-year clocks, recurring gifts and exports.', icon: Gift, subject: 'gifts' },
      { path: 'money/tax-years', label: 'Tax year summaries', description: 'Income and spending in the IHT403 layout, the surplus and gifts made from income.', icon: ClipboardList, subject: 'gifts' },
      { path: 'money/estate', label: 'Estate estimate', description: 'Assets, debts, allowances, gifts within seven years and an indicative inheritance tax figure.', icon: Scale, subject: 'gifts', release: 'R2' },
      { path: 'money/care', label: 'Care finances', description: 'Capital, income, fees and funding position, with a projection to the council thresholds.', icon: HandCoins, subject: 'care' },
      { path: 'money/care/contracts', label: 'Care contracts and invoices', description: 'Care home contracts, invoices, top-ups and NHS-funded nursing care.', icon: BadgePoundSterling, subject: 'care' },
    ],
  },
  {
    key: 'health',
    label: 'Health',
    path: 'health',
    icon: HeartPulse,
    description: 'Evidence for NHS Continuing Healthcare and Attendance Allowance.',
    items: [
      { path: 'health/diary', label: 'Care diary', description: 'Dated notes about care needs, filtered by care domain, with quick capture.', icon: NotebookPen, subject: 'health' },
      { path: 'health/domains', label: 'Care domains', description: 'The 12 care domains with plain-English level descriptions and how much evidence you have.', icon: LayoutGrid, subject: 'health' },
      { path: 'health/chc', label: 'CHC process', description: 'Each stage of the Continuing Healthcare process with dates, deadlines and the evidence pack.', icon: Stethoscope, subject: 'health' },
      { path: 'health/attendance-allowance', label: 'Attendance Allowance', description: 'Eligibility screener, care-needs diary and claim tracker.', icon: Banknote, subject: 'health', release: 'R2' },
    ],
  },
  {
    key: 'records',
    label: 'Records',
    path: 'records',
    icon: FileText,
    description: 'Documents, attorney decisions and reports, and the deprivation schedule.',
    items: [
      { path: 'records/documents', label: 'Documents', description: 'The document vault: categories, search inside documents and the key-documents checklist.', icon: FileText, subject: 'documents' },
      { path: 'records/decisions', label: 'Decisions', description: 'Significant decisions made as attorney, with reasons, templates and co-signature.', icon: ScrollText, subject: 'attorney' },
      { path: 'records/attorney-report', label: 'Attorney report', description: 'Accounts and decisions for a date range, ready for an Office of the Public Guardian request.', icon: ListChecks, subject: 'attorney' },
      { path: 'records/deprivation', label: 'Deprivation schedule', description: 'Gifts in the last seven years with statements and risk indicators for a care assessment.', icon: ShieldCheck, subject: 'gifts' },
    ],
  },
  {
    key: 'more',
    label: 'More',
    path: 'more',
    icon: MoreHorizontal,
    description: 'Family, the Principal, rules and explanations, subscription, your data and help.',
    items: [
      { path: 'more/family', label: 'Family and roles', description: 'Invite family members, choose their role and what each person can see.', icon: Users },
      { path: 'more/principal', label: 'Principal profile and consents', description: 'Personal details, the LPA or deputyship and consent records.', icon: UserRound },
      { path: 'more/activity', label: 'Activity', description: 'Who added or changed what, filterable by area.', icon: Activity },
      { path: 'more/notifications', label: 'Notifications', description: 'Alerts and reminders, and how you want to receive them.', icon: Bell },
      { path: 'more/rules', label: 'Rules version', description: 'The tax and care rules in use, the tax year they apply to and when they were last checked.', icon: BookOpen },
      { path: 'more/explanations', label: 'Explanations library', description: 'Plain-English cards explaining every rule Seventide uses.', icon: HelpCircle },
      { path: 'more/subscription', label: 'Subscription', description: 'Your plan, trial and payments.', icon: CreditCard },
      { path: 'more/data', label: 'Your data', description: 'Download everything, or delete the workspace.', icon: Download },
      { path: 'more/executor', label: 'Executor pack', description: 'Record a date of death, switch to archive mode and build the executor bundle.', icon: Archive, release: 'R3' },
      { path: 'more/security', label: 'Account security', description: 'Passkeys, two-step verification and signed-in devices.', icon: Lock },
      { path: 'more/help', label: 'Help', description: 'How Seventide works and where to get help.', icon: LifeBuoy },
    ],
  },
];

export type ItemState = 'on' | 'off' | 'forbidden';

const ENABLED_KEYS = ['gifts', 'money', 'care', 'health', 'attorney', 'documents'] as const;
function isEnabledKey(s: string): s is keyof EnabledModules {
  return (ENABLED_KEYS as readonly string[]).includes(s);
}

/**
 * 'forbidden' when the member can't view the subject (R-3.3); 'off' when the module isn't switched on for this
 * workspace (FR-1.2) — still reachable from the section landing page; otherwise 'on'.
 */
export function navItemState(item: NavItem, ws: WorkspaceDetail): ItemState {
  if (!item.subject) return 'on';
  if (ws.permissions?.[item.subject]?.view === false) return 'forbidden';
  if (isEnabledKey(item.subject) && ws.enabledModules?.[item.subject] === false) return 'off';
  return 'on';
}

export function sectionByKey(key: SectionKey): NavSection {
  const s = NAV_SECTIONS.find((x) => x.key === key);
  if (!s) throw new Error(`Unknown nav section ${key}`);
  return s;
}

/** Find the nav item for a relative path (used by placeholder pages for their description). */
export function findNavItem(path: string): { section: NavSection; item: NavItem } | null {
  for (const section of NAV_SECTIONS) {
    const item = section.items.find((i) => i.path === path);
    if (item) return { section, item };
  }
  return null;
}

export function wsUrl(wsId: string, path = ''): string {
  return path ? `/w/${wsId}/${path}` : `/w/${wsId}`;
}
